import React from 'react';
import Cookies from 'js-cookie';
import { ActivityCalendar } from 'react-activity-calendar';
import { FiActivity, FiAward, FiBriefcase, FiCalendar, FiCheckCircle, FiClock, FiLogOut, FiMapPin, FiThumbsUp, FiTrendingUp, FiUser, FiUserPlus } from 'react-icons/fi';
import { AreaChart, Area, XAxis, ResponsiveContainer, Tooltip } from 'recharts';
import styles from './Dashboard.module.css';
import * as utils from '../../utils/utils';
import Sidebar from '../Sidebar/Sidebar';

export default class Dashboard extends React.Component {

	constructor(props) {
		super(props);

		this.state = {
			isLoading: true,
			isLoadingMore: false,
			errorText: '',

			firstName: '',
			lastName: '',
			inviteCode: '',
			numInvitesLeft: 0,
			contributionGraph: [],
			totalContributions: 0,
			recentLiveResumes: [],

			offset: 0,
			limit: 5,
			hasMore: false,
		};

		this.sampleSparklineData = [
			{ date: "Wk 1", checkins: 4 },
			{ date: "Wk 2", checkins: 7 },
			{ date: "Wk 3", checkins: 5 },
			{ date: "Wk 4", checkins: 9 },
			{ date: "Wk 5", checkins: 12 },
			{ date: "Wk 6", checkins: 6 },
			{ date: "Wk 7", checkins: 8 },
			{ date: "Wk 8", checkins: 14 },
			{ date: "Wk 9", checkins: 11 },
			{ date: "Wk 10", checkins: 15 },
			{ date: "Wk 11", checkins: 10 },
			{ date: "Wk 12", checkins: 18 }
		];
	}

	componentDidMount() {
		if (!utils.isLoggedIn()) {
			window.location.href = '/signin';
			return;
		}

		this.loadDashboardData();
	}

	getUserIDKey = () => {
		if (utils.isDev) {
			return 'User-Id-Dev';
		}

		return 'User-Id';
	};

	getCurrentUserID = () => {
		const userID = Cookies.get(this.getUserIDKey());
		return Number(userID);
	};

	normalizeCalendarData = (graph) => {
		const maxCount = graph.reduce((max, item) => {
			return Math.max(max, item.count || 0);
		}, 0);

		return graph.map((item) => {
			const count = Number(item.count) || 0;

			let level = 0;
			if (count > 0 && maxCount > 0) {
				level = Math.min(4, Math.max(1, Math.ceil((count / maxCount) * 4)));
			}

			return {
				date: item.day,
				count,
				level,
			};
		});
	};

	loadDashboardData = () => {
		const userID = this.getCurrentUserID();

		if (!Number.isInteger(userID) || userID <= 0) {
			this.setState({
				isLoading: false,
				errorText: 'Could not identify your account. Please sign in again.',
			});
			return;
		}

		Promise.all([
			utils.sendRequest('/profile/getUserProfile', { userID }),
			utils.sendRequest('/profile/getUserContributionGraph', { userID }),
			utils.sendRequest('/profile/getRecentLiveResumeUpdates', { userID, offset: 0 }),
		])
			.then(([profileResponse, contributionResponse, updatesResponse]) => {
				const contributionGraph = this.normalizeCalendarData(contributionResponse.contributionGraph || []);
				const recentLiveResumes = updatesResponse.recentLiveResumes || [];

				this.setState({
					isLoading: false,
					errorText: '',

					firstName: profileResponse.firstName || '',
					lastName: profileResponse.lastName || '',
					inviteCode: profileResponse.inviteCode || '',
					numInvitesLeft: profileResponse.numInvitesLeft || 0,
					contributionGraph,
					totalContributions: contributionResponse.totalContributions || 0,
					recentLiveResumes,

					offset: recentLiveResumes.length,
					limit: updatesResponse.limit || 5,
					hasMore: recentLiveResumes.length === (updatesResponse.limit || 5),
				});
			})
			.catch((error) => {
				console.log('dashboard load error', error);

				this.setState({
					isLoading: false,
					errorText: 'Could not load dashboard data right now. Please try again shortly.',
				});
			});
	};

	loadMoreUpdates = () => {
		const {
			offset,
			limit,
			isLoadingMore,
			recentLiveResumes,
		} = this.state;

		if (isLoadingMore) {
			return;
		}

		const userID = this.getCurrentUserID();
		if (!Number.isInteger(userID) || userID <= 0) {
			return;
		}

		this.setState({
			isLoadingMore: true,
		}, () => {
			utils.sendRequest('/profile/getRecentLiveResumeUpdates', {
				userID,
				offset,
			})
				.then((responseData) => {
					const nextItems = responseData.recentLiveResumes || [];
					const nextLimit = responseData.limit || limit;

					this.setState({
						isLoadingMore: false,
						recentLiveResumes: [...recentLiveResumes, ...nextItems],
						offset: offset + nextItems.length,
						limit: nextLimit,
						hasMore: nextItems.length === nextLimit,
					});
				})
				.catch((error) => {
					console.log('load more updates error', error);
					this.setState({
						isLoadingMore: false,
					});
				});
		});
	};

	onLogoutClick = () => {
		utils.removeAuthDetails();
		window.location.href = '/signin';
	};

	onNewLiveResumeCheckInClick = () => {
		window.location.href = '/live-resume';
	};

	getFullName = () => {
		const {
			firstName,
			lastName,
		} = this.state;

		const fullName = `${firstName} ${lastName}`.trim();
		return fullName === '' ? 'Member' : fullName;
	};

	getInitials = () => {
		const {
			firstName,
			lastName,
		} = this.state;

		const initials = `${firstName.slice(0, 1)}${lastName.slice(0, 1)}`.toUpperCase();
		return initials === '' ? 'ME' : initials;
	};

	getLatestActiveDate = () => {
		const {
			contributionGraph,
		} = this.state;

		for (let index = contributionGraph.length - 1; index >= 0; index--) {
			if ((contributionGraph[index].count || 0) > 0) {
				return contributionGraph[index].date;
			}
		}

		return 'No activity yet';
	};

	formatDate = (value) => {
		if (!value) {
			return 'Unknown';
		}

		const date = new Date(value);
		if (Number.isNaN(date.getTime())) {
			return 'Unknown';
		}

		return date.toLocaleDateString(undefined, {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
		});
	};

	getChipNames = (items, max = 4) => {
		if (!items || items.length === 0) {
			return [];
		}

		return items.slice(0, max).map((item) => item.name).filter(Boolean);
	};

	renderUpdateCard = (item) => {
		const skillNames = this.getChipNames(item.skills);
		const toolNames = this.getChipNames(item.tools);
		const domainNames = this.getChipNames(item.domains, 3);
		const softSkillNames = this.getChipNames(item.softSkills, 3);

		return (
			<article key={item.userEntityLiveResumeId} className={styles.updateCard}>
				<div className={styles.updateTopRow}>
					<div className={styles.updateMetaGroup}>
						<span className={styles.metaChip}><FiBriefcase /> {item.role || 'Role Not Set'}</span>
						<span className={styles.metaChip}><FiMapPin /> {item.location || 'Remote'}</span>
						<span className={styles.metaChip}><FiClock /> {this.formatDate(item.createdAt)}</span>
					</div>
					{/* <span className={styles.scoreChip}><FiCheckCircle /> AI Score {item.aiScore || 0}</span> */}
				</div>

				<p className={styles.liveResumeText}>{item.liveResumeText || 'No live resume update text available.'}</p>

				{(domainNames.length > 0 || skillNames.length > 0 || toolNames.length > 0 || softSkillNames.length > 0 ) && (
					<div className={styles.tagWrapperGrid}>
						{domainNames.length > 0 &&
							<div className={styles.tagBlock}>
								<h4 className={styles.tagHeading}>Domains</h4>
								<div className={styles.tagRow}>
									{domainNames.map((name) => <span key={`domain-${item.userEntityLiveResumeId}-${name}`} className={styles.tag}>{name}</span>)}
								</div>
							</div>
						}

						{skillNames.length > 0 &&
							<div className={styles.tagBlock}>
								<h4 className={styles.tagHeading}>Skills</h4>
								<div className={styles.tagRow}>
									{skillNames.map((name) => <span key={`skill-${item.userEntityLiveResumeId}-${name}`} className={styles.tag}>{name}</span>)}
								</div>
							</div>
						}

						{toolNames.length > 0 &&
							<div className={styles.tagBlock}>
								<h4 className={styles.tagHeading}>Tools</h4>
								<div className={styles.tagRow}>
									{toolNames.map((name) => <span key={`tool-${item.userEntityLiveResumeId}-${name}`} className={styles.tag}>{name}</span>)}
								</div>
							</div>
						}

						{softSkillNames.length > 0 &&
							<div className={styles.tagBlock}>
								<h4 className={styles.tagHeading}>Soft Skills</h4>
								<div className={styles.tagRow}>
									{softSkillNames.map((name) => <span key={`soft-skill-${item.userEntityLiveResumeId}-${name}`} className={styles.tag}>{name}</span>)}
								</div>
							</div>
						}

					</div>
				)}
			</article>
		);
	};

	render() {
		const {
			isLoading,
			isLoadingMore,
			errorText,
			contributionGraph,
			totalContributions,
			recentLiveResumes,
			hasMore,
			numInvitesLeft,
			inviteCode,
		} = this.state;

		return (
			<div className={styles.pageContainer}>
				<Sidebar />

				<div className={styles.glowLayer}>
					<div className={styles.glowTop} />
					<div className={styles.glowBottom} />
				</div>

				<div className={styles.layoutShell}>
					<section className={styles.leftPanel}>
						{/* <div className={styles.brandBadge}>HQVibe</div> */}

						<div className={styles.profileBlock}>
							<div className={styles.avatarCircle}>{this.getInitials()}</div>
							<div>
								<h1 className={styles.title}>{this.getFullName()}</h1>
								<p className={styles.subtitle}>{utils.getUserEmail() || 'Professional Profile'}</p>
							</div>
						</div>

						<div className={styles.metricsGrid}>
							<div className={styles.metricCard}>
								<span className={styles.metricLabel}><FiActivity /> Total Contributions</span>
								<strong className={styles.metricValue}>{totalContributions}</strong>
							</div>

							<div className={styles.metricCard}>
								<span className={styles.metricLabel}><FiTrendingUp /> Last Active</span>
								<strong className={styles.metricValue}>{this.getLatestActiveDate()}</strong>
							</div>

							{/* <div className={styles.metricCard}>
								<span className={styles.metricLabel}><FiCalendar /> Last 90 Days</span>
								<strong className={styles.metricValue}>{contributionGraph.length} tracked days</strong>
							</div> */}

							<div className={styles.metricCard}>
								<span className={styles.metricLabel}><FiAward /> Showing Live Resume Entries</span>
								<strong className={styles.metricValue}>{recentLiveResumes.length}</strong>
							</div>

							{/* <a href="/invite">
								<div className={styles.metricCard}>
									<span className={styles.metricLabel}><FiThumbsUp /> Invite Code</span>
									<strong className={styles.metricValue}>{inviteCode}</strong>
								</div>
							</a>

							<a href="/invite">
								<div className={styles.metricCard}>
									<span className={styles.metricLabel}><FiUserPlus /> Invites Left</span>
									<strong className={styles.metricValue}>{numInvitesLeft}</strong>
								</div>
							</a> */}
						</div>

						<button className={styles.primaryButton} onClick={this.onNewLiveResumeCheckInClick}>
							New Check-in <span className={styles.btnArrow}>→</span>
						</button>
					</section>

					<section className={styles.cardPanel}>
						<div className={styles.sectionHeader}>
							<h2 className={styles.formTitle}><FiUser /> Dashboard Overview</h2>
							<p className={styles.formText}>Track real-time verified activity updates, growth trends, and your live resume ledger.</p>
						</div>

						{errorText !== '' && <div className={styles.errorBox}>{errorText}</div>}

						{isLoading && <div className={styles.loadingBox}>Synchronizing profile indices...</div>}

						{!isLoading && (
							<>
								<section className={styles.contentSection}>
									{/* <h3 className={styles.sectionTitle}>Activity Map</h3>
									<div className={styles.activityCard}>
										<div className={styles.activityHeader}>
											<div>
												<span className={styles.activityKpiLabel}>30-Day Momentum</span>
												<h4 className={styles.activityKpiValue}>(tbd)42 Check-ins</h4>
											</div>
											<span className={styles.activityTrend}>(tbd)+12% vs last month</span>
										</div>

										<div className={styles.activityChartContainer}>
											<ResponsiveContainer width="100%" height="100%">
												<AreaChart data={this.sampleSparklineData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
													<defs>
														<linearGradient id="activityGlow" x1="0" y1="0" x2="0" y2="1">
															<stop offset="5%" stopColor="#2563eb" stopOpacity={0.18} />
															<stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
														</linearGradient>
													</defs>
													<XAxis dataKey="date" hide />
													<Tooltip
														contentStyle={{
															background: 'rgba(15, 23, 42, 0.95)',
															border: '1px solid rgba(255, 255, 255, 0.1)',
															borderRadius: '8px',
															color: '#ffffff',
															fontSize: '12px',
															fontFamily: "'JetBrains Mono', monospace",
															backdropFilter: 'blur(4px)'
														}}
														itemStyle={{ color: '#38bdf8' }}
													/>
													<Area
														type="linear"
														dataKey="checkins"
														stroke="#2563eb"
														strokeWidth={2.5}
														fillOpacity={1}
														fill="url(#activityGlow)"
													/>
												</AreaChart>
											</ResponsiveContainer>
										</div>
									</div> */}
									{/* <ActivityCalendar
										data={contributionGraph}
										blockRadius={3}
										blockSize={12}
										blockMargin={4}
										showWeekdayLabels
										hideColorLegend
										theme={{
											light: ['#f1f5f9', '#cbd5e1', '#94a3b8', '#475569', '#0f172a'],
										}}
										labels={{
											totalCount: '{{count}} items log compiled over trailing 90 days',
										}}
									/> */}
								</section>

								<section className={styles.contentSection}>
									<h3 className={styles.sectionTitle}>Live Resume Streams</h3>

									{recentLiveResumes.length === 0 && (
										<div className={styles.emptyBox}>Stream empty. Initialize live resume entries to populate ledger.</div>
									)}

									<div className={styles.updatesList}>
										{recentLiveResumes.map(this.renderUpdateCard)}
									</div>

									{hasMore && (
										<button
											className={styles.loadMoreButton}
											onClick={this.loadMoreUpdates}
											disabled={isLoadingMore}
										>
											{isLoadingMore ? 'Reading Data...' : 'Fetch Older Entries'}
										</button>
									)}
								</section>
							</>
						)}
					</section>
				</div>
			</div>
		);
	};
};